import { Link } from "react-router-dom";
import { ChevronRight, Award, Check } from "lucide-react";
import { PACKAGES } from "../data/staticData";
import Reveal from "./Reveal";

export default function PackagesPreview() {
  const elite = PACKAGES.find((p) => p.popular) || PACKAGES[PACKAGES.length - 1];
  const others = PACKAGES.filter((p) => p.id !== elite.id);

  return (
    <section
      data-testid="packages-preview-section"
      className="relative bg-[#0A0A0A] border-y border-white/10 py-24 overflow-hidden"
    >
      {/* Ambient lime glow + grid background */}
      <div
        className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#7CFF00] rounded-full blur-[180px] opacity-[0.06] pointer-events-none"
      />
      <div className="absolute inset-0 moving-gradient pointer-events-none" />

      <div className="container-fld relative">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline mb-3">// Multi-State Carry</div>
            <h2 className="font-tactical text-4xl md:text-6xl font-bold uppercase text-white tracking-tight leading-none">
              Permit <span className="text-[#7CFF00] glow-text">Packages.</span>
            </h2>
            <p className="mt-4 max-w-xl text-zinc-400 leading-relaxed">
              Get certified in one state — or build coverage across multiple.
              Train once, carry across borders.
            </p>
          </div>
          <Link
            to="/classes#permit-packages"
            data-testid="packages-preview-view-all"
            className="btn-secondary"
          >
            View All Packages <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {/* ELITE FEATURED CARD */}
          <Reveal className="md:col-span-7">
            <Link
              to={`/booking?course=${encodeURIComponent(elite.name)}`}
              data-testid="packages-preview-elite"
              className="tactical-card group relative block overflow-hidden border-[#7CFF00]/60 shadow-[0_0_40px_rgba(124,255,0,0.18)] h-full"
            >
              {/* Top badge banner */}
              <div className="bg-[#7CFF00] text-black py-1.5 text-center font-tactical text-xs uppercase tracking-widest flex items-center justify-center gap-1.5">
                <Award className="w-3.5 h-3.5" /> Tri-State Defender · Flagship Package
              </div>

              <div className="grid md:grid-cols-5 gap-0">
                {/* Image */}
                <div className="md:col-span-3 aspect-[4/3] md:aspect-auto bg-[#0A0A0A] overflow-hidden relative">
                  <img
                    src={elite.image}
                    alt={elite.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A0A0A]/30 pointer-events-none" />
                </div>

                {/* Details */}
                <div className="md:col-span-2 p-6 md:p-8 flex flex-col">
                  <div className="flex gap-1.5 mb-3">
                    {elite.states.map((s) => (
                      <span
                        key={s}
                        className="font-mono-tactical text-[10px] tracking-widest uppercase px-2 py-1 border border-[#7CFF00]/50 text-[#7CFF00] bg-[#7CFF00]/5"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-tactical text-2xl md:text-3xl uppercase text-white font-bold leading-tight">
                    {elite.name}
                  </h3>
                  <p className="overline mt-1 text-[10px]">{elite.tagline}</p>

                  <ul className="mt-5 space-y-2 flex-1">
                    {elite.features.slice(0, 4).map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed"
                      >
                        <Check className="w-3.5 h-3.5 text-[#7CFF00] mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-5 border-t border-white/10 space-y-3">
                    <div className="flex items-baseline justify-between" data-testid="packages-preview-elite-price">
                      <span className="overline text-[10px]">Starting At</span>
                      <span className="font-tactical text-4xl md:text-5xl font-bold text-[#7CFF00] glow-text tracking-tight">
                        ${elite.price}
                      </span>
                    </div>
                    <div className="font-tactical uppercase tracking-widest text-xs py-3 text-center bg-[#7CFF00] text-black flex items-center justify-center gap-1 group-hover:bg-white transition-colors">
                      Book the Elite Package
                      <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>

          {/* SIDE STACK — 3 other packages */}
          <div className="md:col-span-5 flex flex-col gap-4">
            {others.map((pkg, i) => (
              <Reveal key={pkg.id} delay={150 + i * 80}>
                <Link
                  to="/classes#permit-packages"
                  data-testid={`packages-preview-${pkg.id}`}
                  className="tactical-card group flex gap-4 p-4 hover:border-[#7CFF00]/60 transition-all"
                >
                  <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-[#0A0A0A]">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className="flex gap-1 mb-1.5 items-center">
                      {pkg.states.map((s) => (
                        <span
                          key={s}
                          className="font-mono-tactical text-[9px] tracking-widest uppercase px-1.5 py-0.5 border border-white/15 text-zinc-400"
                        >
                          {s}
                        </span>
                      ))}
                      <span className="ml-auto font-tactical text-lg text-[#7CFF00] font-bold tracking-tight">
                        ${pkg.price}
                      </span>
                    </div>
                    <h4 className="font-tactical text-base uppercase text-white tracking-wider truncate">
                      {pkg.name}
                    </h4>
                    <p className="overline text-[10px] mt-1 truncate">
                      {pkg.tagline}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#7CFF00] group-hover:translate-x-1 transition-all self-center flex-shrink-0" />
                </Link>
              </Reveal>
            ))}

            <Reveal delay={400}>
              <div className="mt-2 flex items-center justify-center gap-3 text-center">
                <span className="h-px w-8 bg-white/15" />
                <span className="overline text-[10px] tracking-[0.3em]">
                  DC + Utah Coming Soon
                </span>
                <span className="h-px w-8 bg-white/15" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
